import { useState, useEffect, useRef } from 'react';
import { TextInput, DataListInput, DateInput } from '../../Input';
import { PrimaryButton, SecondaryButton } from '../../buttons';
import toast from 'react-hot-toast';
import { Search01Icon } from "hugeicons-react";
import api, { API_URL } from '../../../../api';
import CancelConfirmation from '../../../common/CancelConfirmation';


const AddNewMeasureData = ({ setShowNewMeasureFrom, onClose, onAddSuccess, error }) => {
    const [name, setName] = useState('');
    const [measure, setMeasure] = useState([]);
    const [hospital, setHospital] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState(null);
    const [value, setValue] = useState('');
    const [startingDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [dateError, setDateError] = useState('')
    const [isDateValid, setIsDateValid] = useState(false)
    const [isSaving, setIsSaving] = useState(false);
    const [measureUnit, setMeasureUnit] = useState('numerical');
    const formRef = useRef(null);
    const [hasYearData, setHasYearData] = useState(true);
    const [availableMeasures, setAvailableMeasures] = useState([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [availableHospitals, setAvailableHospitals] = useState([]);
    const [hasAvailableHospitals, setHasAvailableHospitals] = useState(false);
    const [hospitalsWithData, setHospitalsWithData] = useState([]);

    const formatDate = (dateObj) => {
        if (!dateObj) return null;

        try {
            if (dateObj.$y && dateObj.$M !== undefined && dateObj.$D) {
                const year = dateObj.$y;
                const month = (dateObj.$M + 1).toString().padStart(2, '0');
                const day = dateObj.$D.toString().padStart(2, '0');

                console.log(`Formatting date from dayjs: ${year}-${month}-${day}`);
                return `${year}-${month}-${day}`;
            }

            const date = new Date(dateObj);
            if (isNaN(date.getTime())) {
                console.error('Invalid date:', dateObj);
                return null;
            }

            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');

            console.log(`Formatting date from Date: ${year}-${month}-${day}`);
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return null;
        }
    };

    const popupRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target) && !showCancelConfirm) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, showCancelConfirm]);

    useEffect(()=>{

        const fetchMeasureUnit = async (year)=>{
            try {
                if (!name || !year) {
                    return;
                }

                const response = await api.get(`${API_URL}/measures/measure_unit/`,
                    { params: {
                        "measure_name": name,
                        measure_year: year
                    } }
                );

                if (response.data && response.data.type) {
                    setMeasureUnit(response.data.type);
                }
            } catch (error) {
                console.error('Error fetching measure unit:', error);
            }
        }

        if (startingDate && startingDate.$y) {
            fetchMeasureUnit(startingDate.$y);
        }

    },[name,startingDate])

    const fetchAvailableHospitals = async (startDate, endDate) => {
        if (!startDate || !endDate) return;

        try {
            const startDateFormatted = formatDate(startDate);
            const endDateFormatted = formatDate(endDate);

            console.log('Fetching hospitals for period:', startDateFormatted, 'to', endDateFormatted);

            const hospitalsWithoutData = [];
            const hospitalsWithExistingData = [];
            
            // First, try to get actual measure data to see which hospitals have submitted data
            let hospitalsWithSubmittedData = new Set();
            let hospitalMeasureMap = new Map();
            
            try {
                const measureDataResponse = await api.get(`${API_URL}/measures/`, {
                    params: {
                        start_date: startDateFormatted,
                        end_date: endDateFormatted
                    }
                });
                
                console.log('Existing measure data response:', measureDataResponse.data);
                
                if (measureDataResponse.data && Array.isArray(measureDataResponse.data.results)) {
                    measureDataResponse.data.results.forEach(measureData => {
                        if (measureData.hospital) {
                            hospitalsWithSubmittedData.add(measureData.hospital);
                            if (!hospitalMeasureMap.has(measureData.hospital)) {
                                hospitalMeasureMap.set(measureData.hospital, []);
                            }
                            hospitalMeasureMap.get(measureData.hospital).push(measureData.measure);
                        }
                    });
                }
                
                console.log('Hospitals with submitted data:', Array.from(hospitalsWithSubmittedData));
                console.log('Hospital measure map:', hospitalMeasureMap);
                
            } catch (error) {
                console.error('Error fetching existing measure data (API might not support date filtering):', error);
                // Continue without existing data - all hospitals will be available
            }
            
            // Now check each hospital for available measures
            for (const hospitalItem of hospital) {
                try {
                    console.log(`Checking hospital: ${hospitalItem.name}`);
                    const response = await api.get(`${API_URL}/measures/by-year/`, {
                        params: {
                            hospital: hospitalItem.name,
                            start_date: startDateFormatted,
                            end_date: endDateFormatted
                        }
                    });
                    
                    console.log(`API response for ${hospitalItem.name}:`, response.data);
                    
                    const measuresData = response.data?.measures || [];
                    const measuresArray = Array.isArray(measuresData) ? measuresData : [];
                    
                    console.log(`Available measures for ${hospitalItem.name}:`, measuresArray);
                    
                    // If there are measures available for submission, include this hospital
                    if (measuresArray.length > 0) {
                        console.log(`${hospitalItem.name} has ${measuresArray.length} measures available for submission`);
                        hospitalsWithoutData.push({
                            ...hospitalItem,
                            availableMeasures: measuresArray.map(m => m.name)
                        });
                    } else {
                        console.log(`${hospitalItem.name} has all measures submitted for this period`);
                        
                        // If we have data from the hospitalMeasureMap, use it
                        const submittedMeasures = hospitalMeasureMap.get(hospitalItem.name) || ['All measures submitted'];
                        
                        hospitalsWithExistingData.push({
                            name: hospitalItem.name,
                            submittedMeasures: submittedMeasures
                        });
                    }
                } catch (error) {
                    console.error(`Error checking measures for ${hospitalItem.name}:`, error);
                    // If there's an error, assume the hospital might have available measures
                    hospitalsWithoutData.push({
                        ...hospitalItem,
                        availableMeasures: []
                    });
                }
            }
            
            console.log('Final hospitals without data:', hospitalsWithoutData);
            console.log('Final hospitals with existing data:', hospitalsWithExistingData);
            
            setAvailableHospitals(hospitalsWithoutData);
            setHospitalsWithData(hospitalsWithExistingData);
            setHasAvailableHospitals(hospitalsWithoutData.length > 0);
            
        } catch (error) {
            console.error('Error fetching available hospitals:', error);
            toast.error('Failed to check hospital availability');
            setHasAvailableHospitals(false);
        }
    };

    const fetchAvailableMeasures = async (hospital, startDate, endDate) => {
        if (!hospital || !startDate || !endDate) return;

        try {
            const startDateFormatted = formatDate(startDate);
            const endDateFormatted = formatDate(endDate);

            const response = await api.get(`${API_URL}/measures/by-year/`, {
                params: {
                    hospital: hospital.name,
                    start_date: startDateFormatted,
                    end_date: endDateFormatted
                }
            });
            
            const measuresData = response.data?.measures || [];
            const measuresArray = Array.isArray(measuresData) ? measuresData.map(item => item.name) : [];
            setMeasure(measuresArray);
            setAvailableMeasures(measuresArray);
            
            return measuresArray;
        } catch (error) {
            console.error('Error fetching measures:', error);
            toast.error('Failed to load measures');
            return [];
        }
    };




    useEffect(() => {
        if (!startingDate || !endDate) {
            setIsDateValid(false);
            setHasAvailableHospitals(false);
            setAvailableHospitals([]);
            return;
        }

        try {
            if (startingDate.$y === undefined || endDate.$y === undefined) {
                setIsDateValid(false);
                setHasAvailableHospitals(false);
                return;
            }

            if (startingDate.$y !== endDate.$y) {
                setIsDateValid(false);
                setDateError("Both Starting Date and Ending date should be in the same year.");
                setHasAvailableHospitals(false);
                return;
            }

            if (startingDate.$m > endDate.$m || (startingDate.$m === endDate.$m && startingDate.$d > endDate.$d)) {
                setIsDateValid(false);
                setDateError("Ending date should be greater than Starting Date.");
                setHasAvailableHospitals(false);
                return;
            }

            setIsDateValid(true);
            setDateError('');
            // Reset hospital and measure selections when dates change
            setSelectedHospital(null);
            setName('');
            setMeasure([]);
            setAvailableMeasures([]);
            
            // Fetch available hospitals for the selected date range
            if (hospital.length > 0) {
                fetchAvailableHospitals(startingDate, endDate);
            }
        } catch (error) {
            console.error('Error validating dates:', error.message);
            setIsDateValid(false);
            setHasAvailableHospitals(false);
        }
    }, [startingDate, endDate, hospital]);

    useEffect(() => {
        if (selectedHospital && startingDate && endDate && isDateValid) {
            fetchAvailableMeasures(selectedHospital, startingDate, endDate);
        } else {
            setMeasure([]);
            setAvailableMeasures([]);
        }
    }, [selectedHospital, startingDate, endDate, isDateValid]);

    useEffect(() => {
        const fetchHospitals = async () => {
            try {
                const response = await api.get(`${API_URL}/hospitals/`);

                let hospitalsData = [];
                if (response.data && response.data.results && Array.isArray(response.data.results)) {
                    hospitalsData = response.data.results;
                } else if (Array.isArray(response.data)) {
                    hospitalsData = response.data;
                }

                setHospital(hospitalsData);
            } catch (error) {
                toast.error('Failed to load hospitals');
            }
        };
        fetchHospitals();
    }, []);


    const submitMeasureData = async (measureData) => {
        setIsSaving(true);
        try {
            const response = await api.post(`${API_URL}/measures/new_measure_data/`, measureData);

            if (response.status === 201 || response.status === 200) {
                toast.success('Measure added successfully');

                setShowNewMeasureFrom(false);
                onClose();
                if (typeof onAddSuccess === 'function') {
                    onAddSuccess();
                }
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            }
        } catch (error) {
            console.error('Error adding measure:', error.response?.data || error.message);
            toast.error(error.response?.data?.error || 'Failed to add measure. Please try again.');
            setIsSaving(false);
        }
    };




    const handleCancelClick = (e) => {
        e.preventDefault();
        const hasUnsavedData = name || selectedHospital || value || startingDate || endDate;

        if (hasUnsavedData) {
            setShowCancelConfirm(true);
        } else {
            onClose();
        }
    };

    const confirmCancel = () => {
        setShowCancelConfirm(false);
        onClose();
    };

    const cancelCancel = () => {
        setShowCancelConfirm(false);
    };

    return (
        <div className='form' ref={formRef}>
            <h2>New Measure Data</h2>
            <form className='new-measure-form' ref={popupRef}>
                <div className='date-range'>
                    <div className='date'>
                        <label>Start Date</label>
                        <div
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <DateInput
                                date={startingDate}
                                setDate={setStartDate}
                                placeholder="Starting date"
                                label="Starting date"
                                choices={['day', 'month', 'year']}

                            />
                        </div>
                    </div>
                    <div className='date'>
                        <label>End Date</label>
                        <div
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}>
                            <DateInput

                                date={endDate}
                                setDate={setEndDate}
                                placeholder="End date"
                                label="End date"
                                choices={['day', 'month', 'year']}
                            />
                        </div>
                    </div>

                </div>


                {startingDate && endDate ?
                    isDateValid ?
                        hasAvailableHospitals ? (
                            <>
                                <DataListInput
                                    iconClass={<Search01Icon size={12} />}
                                    id="hospital"
                                    name="hospitalName"
                                    placeholder="Hospital"
                                    value={selectedHospital?.name || ''}
                                    setValue={(name) => setSelectedHospital(availableHospitals.find(h => h.name === name) || null)}
                                    options={availableHospitals.map(h => h.name) || []}
                                />

                                {selectedHospital && availableMeasures.length > 0 && (
                                    <>
                                        <DataListInput
                                            iconClass={<Search01Icon size={12}/>}
                                            id="measure"
                                            name="measureName"
                                            placeholder="Measure name"
                                            value={name}
                                            setValue={setName}
                                            options={availableMeasures || []}
                                        />

                                        {name && (
                                            <div className='values'>
                                            <label for="value">This field only accepts numerical values do not enter text or symbols</label>
                                            <div className='label-text'>

                                                <TextInput
                                                    type='number'
                                                    name='value'
                                                    placeholder='Value'
                                                    value={value}
                                                    setValue={setValue}
                                                />

                                                 <p className='symbols'>
                                                 {measureUnit === "percentage"? "%":"#"}
                                                 </p>
                                            </div>

                                                {error && <p style={{ color: 'red' }}>{error}</p>}

                                            </div>
                                        )}

                                        <div className="buttons">
                                            <PrimaryButton
                                                buttonText='Save'
                                                onClick={() => {
                                                    if (!name || !selectedHospital || !value) {
                                                        toast.error('Please fill all fields');
                                                        return;
                                                    }

                                                    const startingDateFormatted = formatDate(startingDate);
                                                    const endingDateFormatted = formatDate(endDate);
                                                    if (!startingDateFormatted || !endingDateFormatted) {
                                                        toast.error('Invalid date format');
                                                        return;
                                                    }

                                                    const newMeasureData = {
                                                        measure: name,
                                                        hospital: selectedHospital?.name,
                                                        value,
                                                        starting_date: startingDateFormatted,
                                                        end_date: endingDateFormatted
                                                    };

                                                    submitMeasureData(newMeasureData);
                                                }}
                                                isLoading={isSaving}
                                                processingText='Saving'
                                            />
                                            <SecondaryButton
                                                onClick={handleCancelClick}
                                                buttonText='Cancel'
                                                type='button'
                                            />
                                        </div>
                                    </>
                                )}

                            </>
                        ) : (
                            <div style={{ color: '#666', marginTop: '20px' }}>
                                <p style={{ fontWeight: 'bold', marginBottom: '10px', color: '#2c3e50' }}>
                                    No hospitals need data entry for the selected period.
                                </p>
                                {hospitalsWithData.length > 0 ? (
                                    <div>
                                        <p style={{ marginBottom: '8px' }}>
                                            The following hospitals have already submitted all required measures for this period:
                                        </p>
                                        <ul style={{ paddingLeft: '20px', margin: '0' }}>
                                            {hospitalsWithData.map((hospital, index) => (
                                                <li key={index} style={{ marginBottom: '8px' }}>
                                                    <strong style={{ color: '#27ae60' }}>{hospital.name}</strong>
                                                    {hospital.submittedMeasures && hospital.submittedMeasures.length > 0 && (
                                                        <ul style={{ paddingLeft: '15px', fontSize: '13px', color: '#555', marginTop: '4px' }}>
                                                            {hospital.submittedMeasures.map((measure, idx) => (
                                                                <li key={idx}>✓ {measure}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                        <p style={{ marginTop: '15px', fontSize: '14px', fontStyle: 'italic', color: '#7f8c8d' }}>
                                            Try selecting a different reporting period or contact your administrator if you need to modify existing data.
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        <p style={{ marginBottom: '8px', color: '#27ae60' }}>
                                            Great! All hospitals are ready for data submission.
                                        </p>
                                        <p style={{ fontSize: '14px', color: '#7f8c8d' }}>
                                            You should see the hospital selection above. If not, there might be a technical issue.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )
                        :
                        <p className="date-error">{dateError}</p>
                    :
                    <p>Please select reporting period</p>
                }


            </form>

            <CancelConfirmation
                isOpen={showCancelConfirm}
                onKeepEditing={cancelCancel}
                onConfirmCancel={confirmCancel}
            />

        </div>
    );
}

export default AddNewMeasureData;
